import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { Destroyed } from 'src/app/utils/directives/destroyed.directive';
import {YoungEditInformationDto} from '../../../model/dto/young-edit-information.dto';
import {PSchoolLevel} from '../../../model/param/p-school-level.model';
import { FormSelectItem } from 'src/app/model/form/select-item.model';
import { PScolarDifficulty } from 'src/app/model/param/p-scolar-difficulty';
import { ParamWebservice } from 'src/app/services/webservices/param.webservice';
import { PDisciplineScolarDifficulty } from 'src/app/model/param/p-discipline-scolar-difficulty';
import {dateInscription} from '../../../../environments/environment';
import {YoungCreationDataService} from '../../../services/young-creation-data.service';
import {AlertService} from '../../../services/alert.service';
import { AlertComponent } from '../../../utils/alert/alert/alert.component';
import { SelectMultipleComponent } from 'src/app/utils/form-parts/form-items/select-multiple/select-multiple.component';
import { isNullOrUndefined } from 'src/app/utils/utils.static';
import { SchoolLevelInputComponent } from '../inscription/school-level-input/school-level-input.component';
import { NgIf } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
    selector: 'app-inscription-information-young',
    templateUrl: './inscription-information-young.component.html',
    styleUrls: ['./inscription-information-young.component.scss'],
    imports: [FormsModule, MatSlideToggleModule, NgIf, SchoolLevelInputComponent, AlertComponent, SelectMultipleComponent]
})
export class InscriptionInformationYoungComponent extends Destroyed implements OnInit {
    private readonly youngCreationDataService = inject(YoungCreationDataService);
    private readonly alertService = inject(AlertService);
    private readonly paramWebservice = inject(ParamWebservice);
    private readonly fb = inject(UntypedFormBuilder);

    @Input() young: YoungEditInformationDto;
    @Input() pSchoolLevelsArray: PSchoolLevel[] = [];
    @Input() pSchoolLevelsAllValues: PSchoolLevel[] = [];
    formValid = false;
    schoolYearList = [];

    formGroup: UntypedFormGroup;
    scolarDifficultyForSelect: FormSelectItem[] = [];
    disciplineScolarDifficultyForSelect: FormSelectItem[] = [];
    scolarDifficulty: number[] = [];
    disciplineScolarDifficulty: number[] = [];

    @Output()
        nextStep: EventEmitter<void> = new EventEmitter<void>();

    get fg() {
        return this.formGroup.controls;
    }

    get isInvalidDisciplineDIfficulteScolaire() {
        return this.fg.scolarDifficulty.value?.includes(2)
          && (isNullOrUndefined(this.fg?.disciplineScolarDifficulty) || this.fg.disciplineScolarDifficulty.value.length === 0);
    }

    get hasSchoolDifficulties(): boolean {
        const noDifficultyOptionId = 2;
        return !!this.fg?.scolarDifficulty?.value?.includes(noDifficultyOptionId);
    }

    ngOnInit(): void {
        this.initSchoolYear();
        this.initScolarDifficulty();
        this.initDisciplineScolarDifficulty();

        this.formGroup = this.fb.group({
            scolarDifficulty: [[]],
            disciplineScolarDifficulty: [[]]
        });
    }

    updateYoung() {
        // regle de validation : - avoir le niveau scolaire
        if (this.young.schoolLevel.schoolName && !this.young.schoolLevel.idPSchoolLevel) {
            this.alertService.clear('alert-step-1');
            this.alertService.error(
                'Veuillez remplir la classe', 'alert-step-1'
            );
            return;
        }

        if (this.isInvalidDisciplineDIfficulteScolaire) {
            this.alertService.clear('alert-step-1');
            this.alertService.error(
                'Veuillez remplir la matière', 'alert-step-1'
            );
            return;
        }

        if (this.formValid) {
            this.young.scolarDifficultyIds = this.fg.scolarDifficulty.value;
            this.young.disciplineScolarDifficultyIds = this.fg.disciplineScolarDifficulty.value;
            this.youngCreationDataService.saveInDb(this.young);
            this.alertService.clear('alert-step-1');
            this.alertService.success(
                'Modifications enregistrées.', 'alert-step-1'
            );

            setTimeout(() => { // Permet le visionnage du message d'enregistrement réussi
                this.nextStep.emit();
                this.formValid = false;
            }, 1000);
        }
    }

    activForm() {
        this.formValid = !!(!this.isInvalidDisciplineDIfficulteScolaire
          && (this.young?.schoolLevel?.idPSchoolLevel
            || (this.young?.schoolLevel?.idPSchoolLevel === undefined)
          ));
    }

    checkActivHandicap() {
        // en base handicap est un string et on a besoin d'un boolean
        return this.young.handicap && this.young.handicap.toLowerCase() === 'true';
    }

    activFormHandicap() {
        if (this.young.handicap && this.young.handicap.toLowerCase() === 'true') {
            this.young.handicap = 'false';
        } else {
            this.young.handicap = 'true';
        }
        this.activForm();
    }

    initSchoolYear() {
        const yearActual = new Date();
        const yearMin = new Date((yearActual.getFullYear() - 15).toString()).getFullYear();
        let yearMax;

        if (yearActual > dateInscription.limitDate
            && (yearActual.getFullYear() - dateInscription.limitDate.getFullYear() < 1)) {
            yearMax = yearActual.getFullYear() + 1;
        } else {
            yearMax = yearActual.getFullYear();
        }

        for (let i = yearMax; i > yearMin; i--) {
            this.schoolYearList.push((i - 1) + '/' + i);
        }
    }

    private initScolarDifficulty() {
        this.paramWebservice.getAllScolarDifficulty().pipe(takeUntil(this.destroyed)).subscribe(
            (content: PScolarDifficulty[]) => {
            content.forEach(scolarDifficulty => {
                const option = new FormSelectItem();
                option.id = scolarDifficulty.id;
                option.name = scolarDifficulty.label;
                this.scolarDifficultyForSelect.push(option);
            });
        });
    }

    private initDisciplineScolarDifficulty() {
        this.paramWebservice.getAllDisciplineScolarDifficulty().pipe(takeUntil(this.destroyed)).subscribe(
            (content: PDisciplineScolarDifficulty[]) => {
            content.forEach(disciplineScolarDifficulty => {
                const option = new FormSelectItem();
                option.id = disciplineScolarDifficulty.id;
                option.name = disciplineScolarDifficulty.label;
                this.disciplineScolarDifficultyForSelect.push(option);
            });
        });
    }

    parentChangeLevel() {
        this.activForm();
    }

    emitNextStep() {
        this.nextStep.emit();
    }

    checkIfDataIsPresent() {
        return this.pSchoolLevelsAllValues && this.pSchoolLevelsAllValues.length > 0 && this.young.schoolLevel && this.pSchoolLevelsArray && this.pSchoolLevelsArray.length > 0;
    }

}
